import { Button } from "@/ds/shadcn/components/ui/button";
import { Input } from "@/ds/shadcn/components/ui/input";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { DataView } from "@/features/common/components/dataview/DataView";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import type { GetImportResponseModelInvalidRow, GetImportStatusResponseModelImport } from "@/features/import/api/useImportStatus";
import { useUpdateImportRow } from "@/features/import/api/useUpdateImportRow";
import { useNotifyMutationError } from "@/lib/common/useNotifyMutationError";
import type { ViewType } from "@/lib/common/ViewType";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { CheckIcon, X } from "lucide-react";

export interface ImportEditorProps {
	import: GetImportStatusResponseModelImport;
}

export const ImportEditor = (props: ImportEditorProps): React.JSX.Element => {
	return (
		<>
			<div className="border-b">
				<div className="type-item flex justify-center @md:justify-start gap-6 p-3">
					<div className="flex items-center gap-1 text-primary">
						<CheckIcon className="w-4" />
						{props.import.validRowCount} Valid Rows
					</div>

					<div className="flex items-center gap-1 text-destructive">
						<X className="w-4" />
						{props.import.invalidRowCount} Invalid Rows
					</div>
				</div>

				{
					props.import.invalidRowCount === 0 &&
					<div className="type-description text-center @md:text-left border-t p-3">
						All rows in your uploaded CSV document are valid.
					</div>
				}

				{
					props.import.invalidRowCount > 0 &&
					<div className="type-description text-center @md:text-left border-t p-3">
						Some rows in your uploaded CSV document are invalid and require fixing.
						You can fix them below, or abort the import and try again later.
					</div>
				}
			</div>

			{
				props.import.invalidRowCount > 0 && (
					<div className="@md:border-b">
						<DataView
							onlyUseGalleryForMobile
							items={props.import.invalidRows}
							getKey={x => x.importRowId}
							additionalProps={{ firstInvalidRow: props.import.invalidRows[0]?.importRowId }}
							galleryComponent={ImportEditorGalleryView}
							listComponent={ImportEditorListView}
							listViewHeaderClassNames={[
								"w-[10%] @2xl:w-[14%]",
								"w-[16%] @2xl:w-[14%]",
								"w-[16%] @2xl:w-[14%]",
								"w-[14%]",
								"w-[12%] @2xl:w-[14%]",
								"w-[16%] @2xl:w-[14%]",
								"w-[16%]"
							]}
							listViewHeaderLabels={[
								"Quantity",
								"Identifier",
								"Action",
								"Unit Amount",
								"Currency",
								"Date",
								""
							]} />
					</div>
				)
			}
		</>
	);
};

interface ImportRowProps extends GetImportResponseModelInvalidRow {
	firstInvalidRow: string;
}

interface ImportEditorRowProps extends GetImportResponseModelInvalidRow {
	layout: ViewType;
}

const ImportEditorListView: FC<ImportRowProps> = props => {
	return props.importRowId === props.firstInvalidRow
		? <ImportEditorRow {...props} layout="List" />
		: <ImportDisplayRowListView {...props} />;
};

const ImportEditorRow: FC<ImportEditorRowProps> = props => {
	const [quantityText, setQuantityText] = useState(props.quantityText);
	const [fcIdText, setFcIdText] = useState(props.fcIdText);
	const [actionText, setActionText] = useState(props.actionText);
	const [unitAmountText, setUnitAmountText] = useState(props.unitAmountText);
	const [currencyText, setCurrencyText] = useState(props.currencyText);
	const [dateText, setDateText] = useState(props.dateText);

	const updateImportRow = useUpdateImportRow();
	useNotifyMutationError(updateImportRow);

	useEffect(() => {
		setQuantityText(props.quantityText);
		setFcIdText(props.fcIdText);
		setActionText(props.actionText);
		setUnitAmountText(props.unitAmountText);
		setCurrencyText(props.currencyText);
		setDateText(props.dateText);
	}, [
		props.quantityText,
		props.fcIdText,
		props.actionText,
		props.unitAmountText,
		props.currencyText,
		props.dateText
	]);

	const hasChanges = useMemo(() => {
		return quantityText !== props.quantityText
			|| fcIdText !== props.fcIdText
			|| actionText !== props.actionText
			|| unitAmountText !== props.unitAmountText
			|| currencyText !== props.currencyText
			|| dateText !== props.dateText;
	}, [
		quantityText,
		fcIdText,
		actionText,
		unitAmountText,
		currencyText,
		dateText,
		props.quantityText,
		props.fcIdText,
		props.actionText,
		props.unitAmountText,
		props.currencyText,
		props.dateText
	]);

	const errorMessage = props.quantityError
		|| props.fcIdError
		|| props.actionError
		|| props.unitAmountError
		|| props.currencyError
		|| props.dateError;

	const onClick = useCallback(() => {
		if (!hasChanges) {
			return;
		}

		updateImportRow.mutate({
			importRowId: props.importRowId,
			quantityText,
			fcIdText,
			actionText,
			unitAmountText,
			currencyText,
			dateText
		});
	}, [
		hasChanges,
		updateImportRow,
		quantityText,
		fcIdText,
		actionText,
		unitAmountText,
		currencyText,
		dateText,
		props.importRowId
	]);

	const quantityInput = (
		<Input
			className="text-xs focus-visible:ring-0 focus-visible:border-input"
			aria-invalid={!!props.quantityError && !hasChanges}
			value={quantityText}
			onChange={e => setQuantityText(e.target.value)} />
	);

	const fcIdInput = (
		<Input
			className="text-xs focus-visible:ring-0 focus-visible:border-input"
			aria-invalid={!!props.fcIdError && !hasChanges}
			value={fcIdText}
			onChange={e => setFcIdText(e.target.value)} />
	);

	const actionInput = (
		<Input
			className="text-xs focus-visible:ring-0 focus-visible:border-input"
			aria-invalid={!!props.actionError && !hasChanges}
			value={actionText}
			onChange={e => setActionText(e.target.value)} />
	);

	const unitAmountInput = (
		<Input
			className="text-xs focus-visible:ring-0 focus-visible:border-input"
			aria-invalid={!!props.unitAmountError && !hasChanges}
			value={unitAmountText}
			onChange={e => setUnitAmountText(e.target.value)} />
	);

	const currencyInput = (
		<Input
			className="text-xs focus-visible:ring-0 focus-visible:border-input"
			aria-invalid={!!props.currencyError && !hasChanges}
			value={currencyText}
			onChange={e => setCurrencyText(e.target.value)} />
	);

	const dateInput = (
		<Input
			className="text-xs focus-visible:ring-0 focus-visible:border-input"
			aria-invalid={!!props.dateError && !hasChanges}
			value={dateText}
			onChange={e => setDateText(e.target.value)} />
	);

	if (props.layout === "Gallery") {
		return (
			<div className="flex flex-col gap-4">
				{
					errorMessage && (
						<div className="text-center text-xs text-destructive">
							{errorMessage}
						</div>
					)
				}

				<div>
					<div className="type-meta pb-3">Quantity</div>
					{quantityInput}
				</div>

				<div>
					<div className="type-meta pb-3">Identifier</div>
					{fcIdInput}
				</div>

				<div>
					<div className="type-meta pb-3">Action</div>
					{actionInput}
				</div>

				<div>
					<div className="type-meta pb-3">Unit Amount</div>
					{unitAmountInput}
				</div>

				<div>
					<div className="type-meta pb-3">Currency</div>
					{currencyInput}
				</div>

				<div>
					<div className="type-meta pb-3">Date</div>
					{dateInput}
				</div>

				<Button
					variant="outline"
					className="text-xs"
					disabled={!hasChanges || updateImportRow.isPending}
					onClick={onClick}>

					{
						updateImportRow.isPending
							? <Spinner />
							: "Save Changes"
					}
				</Button>
			</div>
		);
	}

	return (
		<ListItem error={errorMessage}>
			<TableCell>
				{quantityInput}
			</TableCell>

			<TableCell>
				{fcIdInput}
			</TableCell>

			<TableCell>
				{actionInput}
			</TableCell>

			<TableCell>
				{unitAmountInput}
			</TableCell>

			<TableCell>
				{currencyInput}
			</TableCell>

			<TableCell>
				{dateInput}
			</TableCell>

			<TableCell>
				<div className="flex justify-end">
					<Button
						variant="outline"
						className="text-xs @md:w-28"
						disabled={!hasChanges || updateImportRow.isPending}
						onClick={onClick}>
						{
							updateImportRow.isPending
								? <Spinner />
								: "Save Changes"
						}
					</Button>
				</div>
			</TableCell>
		</ListItem>
	);
};

const ImportDisplayRowListView: FC<GetImportResponseModelInvalidRow> = props => {
	return (
		<ListItem>
			<TableCell className="text-xs @md:text-sm">
				{props.quantityText}
			</TableCell>

			<TableCell className="text-xs @md:text-sm">
				{props.fcIdText}
			</TableCell>

			<TableCell className="text-xs @md:text-sm">
				{props.actionText}
			</TableCell>

			<TableCell className="text-xs @md:text-sm">
				{props.unitAmountText}
			</TableCell>

			<TableCell className="text-xs @md:text-sm">
				{props.currencyText}
			</TableCell>

			<TableCell className="text-xs @md:text-sm">
				{props.dateText}
			</TableCell>
		</ListItem>
	);
};

const ImportEditorGalleryView: FC<ImportRowProps> = props => {
	if (props.importRowId !== props.firstInvalidRow) {
		return <></>;
	}

	return <ImportEditorRow {...props} layout="Gallery" />;
};
